import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LanguageLocaleEnum, MatchDayService } from "../client/api";
import { MatchSmallElement } from "../config/types";
import { formatRelativeDateLabel, isFiniteDate, toLocalMidnight } from "../config/utils";
import { toYmd } from "../config/routes";
import { AppDispatch, RootState } from "../store";
import {
    fetchMatchesByLeagueAndDate,
    fetchMatchesBySubCategoryAndDate,
} from "../store/actions/matchActions";
import { selectSportsType } from "../selectors";

type UseCupMatchesParams = {
    isCup: boolean;
    leagueId?: number;
    leagueSubCategoryId?: number;
    aoi: number;
    sports: ReturnType<typeof selectSportsType>;
    language: LanguageLocaleEnum;
};

type UseCupMatchesResult = {
    selectedCupLeagueId: number | "all" | undefined;
    availableDateObjects: Date[];
    selectedDate?: string;
    selectedDateLabel: string;
    displayMatches: MatchSmallElement[];
    handleMatchesLeagueChange: (value: number | string | undefined) => void;
    handlePrevDate: () => void;
    handleNextDate: () => void;
    handleDateSelect: (date: Date) => void;
};

export function useCupMatches({
    isCup,
    leagueId,
    leagueSubCategoryId,
    aoi,
    sports,
    language,
}: UseCupMatchesParams): UseCupMatchesResult {
    const dispatch = useDispatch<AppDispatch>();
    const matchElements = useSelector((s: RootState) => s.match.matchElements);
    const [selectedCupLeagueId, setSelectedCupLeagueId] = useState<number | "all" | undefined>(undefined);
    const [allCupMatchesCache, setAllCupMatchesCache] = useState<Record<number | string, MatchSmallElement[]>>({});
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
    const currentlyFetchingDateRef = useRef<string | null>(null);
    const lastCachedMatchElementsRef = useRef<any>(null);

    const resetDates = useCallback(() => {
        setAvailableDates([]);
        setSelectedDate(undefined);
    }, []);

    const findDefaultDate = useCallback((dates: string[]): string => {
        if (dates.length === 0) return "";

        const today = toLocalMidnight(new Date());
        const todayStr = toYmd(today);

        const pastDates = dates.filter((dateStr) => dateStr <= todayStr);

        if (pastDates.length > 0) {
            return pastDates[pastDates.length - 1];
        }

        return dates[0];
    }, []);

    useEffect(() => {
        if (isCup && leagueId) {
            setSelectedCupLeagueId(leagueId);
        } else {
            setSelectedCupLeagueId(undefined);
        }
    }, [isCup, leagueId]);

    useEffect(() => {
        setAllCupMatchesCache({});
        resetDates();
    }, [leagueId, resetDates]);

    useEffect(() => {
        if (!isCup || !selectedCupLeagueId) {
            resetDates();
            return;
        }

        resetDates();

        if (selectedCupLeagueId === "all" && leagueSubCategoryId && aoi) {
            MatchDayService.getMatchDatesTodayMatchDayTodayGet(aoi, undefined, undefined, sports, leagueSubCategoryId)
                .then((dates: string[]) => {
                    if (dates && dates.length > 0) {
                        setAvailableDates(dates);
                        setSelectedDate(findDefaultDate(dates));
                    }
                })
                .catch(() => {
                    setAvailableDates([]);
                });
        } else if (typeof selectedCupLeagueId === "number") {
            MatchDayService.getMatchDatesTodayMatchDayTodayGet(
                undefined,
                selectedCupLeagueId,
                undefined,
                sports,
                undefined
            )
                .then((dates) => {
                    if (dates && dates.length > 0) {
                        setAvailableDates(dates);
                    }
                })
                .catch(() => {
                    setAvailableDates([]);
                });
        }
    }, [isCup, selectedCupLeagueId, leagueSubCategoryId, aoi, sports, resetDates, findDefaultDate]);

    useEffect(() => {
        if (!matchElements || !isCup) return;

        if (lastCachedMatchElementsRef.current === matchElements) return;

        const newMatches = matchElements.response as unknown as MatchSmallElement[];
        if (!newMatches || newMatches.length === 0) return;

        const dateToCache = currentlyFetchingDateRef.current;
        if (!dateToCache) return;

        lastCachedMatchElementsRef.current = matchElements;

        const cacheKey = selectedCupLeagueId === "all" && leagueSubCategoryId
            ? `sub-${leagueSubCategoryId}-${dateToCache}`
            : `${matchElements.id}-${dateToCache}`;

        setAllCupMatchesCache((prev) => ({
            ...prev,
            [cacheKey]: newMatches,
        }));
        currentlyFetchingDateRef.current = null;
    }, [matchElements, isCup, selectedCupLeagueId, leagueSubCategoryId]);

    useEffect(() => {
        if (!isCup || selectedCupLeagueId !== "all" || !selectedDate) return;

        lastCachedMatchElementsRef.current = null;
        currentlyFetchingDateRef.current = selectedDate;

        if (leagueSubCategoryId && aoi) {
            fetchMatchesBySubCategoryAndDate(dispatch, leagueSubCategoryId, aoi, selectedDate);
        }
    }, [dispatch, isCup, selectedCupLeagueId, leagueSubCategoryId, aoi, selectedDate]);

    useEffect(() => {
        if (!isCup || typeof selectedCupLeagueId !== "number") return;
        if (availableDates.length === 0) return;
        if (currentlyFetchingDateRef.current) return;

        const nextMissingDate = availableDates.find(
            (dateStr) => !allCupMatchesCache[`${selectedCupLeagueId}-${dateStr}`]
        );

        if (!nextMissingDate) return;

        lastCachedMatchElementsRef.current = null;
        currentlyFetchingDateRef.current = nextMissingDate;
        fetchMatchesByLeagueAndDate(dispatch, selectedCupLeagueId, nextMissingDate);
    }, [dispatch, isCup, selectedCupLeagueId, availableDates, allCupMatchesCache]);

    const matches = useMemo(() => {
        const targetLeagueId =
            isCup && selectedCupLeagueId && selectedCupLeagueId !== "all"
                ? selectedCupLeagueId
                : leagueId;

        if (matchElements && matchElements.id === targetLeagueId) {
            return matchElements.response as unknown as MatchSmallElement[];
        }
        return [];
    }, [matchElements, leagueId, isCup, selectedCupLeagueId]);

    const cupMatchesForDate = useMemo(() => {
        if (!isCup || !selectedDate) return [];

        const cacheKey =
            selectedCupLeagueId === "all" && leagueSubCategoryId
                ? `sub-${leagueSubCategoryId}-${selectedDate}`
                : typeof selectedCupLeagueId === "number"
                ? `${selectedCupLeagueId}-${selectedDate}`
                : null;

        if (!cacheKey) return [];

        const cachedMatches = allCupMatchesCache[cacheKey];

        return cachedMatches ? [...cachedMatches].sort((a, b) => a.startTime - b.startTime) : [];
    }, [isCup, selectedCupLeagueId, selectedDate, allCupMatchesCache, leagueSubCategoryId]);

    const allMatchesForSelectedCupLeague = useMemo(() => {
        if (!isCup || typeof selectedCupLeagueId !== "number") return [];

        const entries = Object.entries(allCupMatchesCache).filter(([key]) =>
            key.startsWith(`${selectedCupLeagueId}-`)
        );

        if (entries.length === 0) return [];

        const flattened = entries.flatMap(([, value]) => value);
        return flattened.sort((a, b) => a.startTime - b.startTime);
    }, [isCup, selectedCupLeagueId, allCupMatchesCache]);

    const displayMatches = useMemo(() => {
        if (!isCup) {
            return matches;
        }

        if (selectedCupLeagueId === "all" && selectedDate) {
            return cupMatchesForDate;
        }

        if (typeof selectedCupLeagueId === "number") {
            return allMatchesForSelectedCupLeague;
        }

        return matches;
    }, [isCup, selectedCupLeagueId, selectedDate, cupMatchesForDate, matches, allMatchesForSelectedCupLeague]);

    const availableDateObjects = useMemo(
        () =>
            availableDates
                .map((dateStr) => {
                    const [year, month, day] = dateStr.split("-").map((n) => parseInt(n, 10));
                    return toLocalMidnight(new Date(year, month - 1, day));
                })
                .filter((d) => isFiniteDate(d)),
        [availableDates]
    );

    const selectedDateLabel = useMemo(() => {
        if (!selectedDate) return "";
        const [year, month, day] = selectedDate.split("-").map((n) => parseInt(n, 10));
        const date = toLocalMidnight(new Date(year, month - 1, day));
        if (!isFiniteDate(date)) return "";
        return formatRelativeDateLabel(date, language);
    }, [selectedDate, language]);

    const handlePrevDate = useCallback(() => {
        if (!selectedDate || availableDates.length === 0) return;
        const currentIndex = availableDates.indexOf(selectedDate);
        if (currentIndex > 0) {
            setSelectedDate(availableDates[currentIndex - 1]);
        }
    }, [selectedDate, availableDates]);

    const handleNextDate = useCallback(() => {
        if (!selectedDate || availableDates.length === 0) return;
        const currentIndex = availableDates.indexOf(selectedDate);
        if (currentIndex < availableDates.length - 1) {
            setSelectedDate(availableDates[currentIndex + 1]);
        }
    }, [selectedDate, availableDates]);

    const handleDateSelect = useCallback(
        (date: Date) => {
            const dateStr = toYmd(date);
            if (availableDates.includes(dateStr)) {
                setSelectedDate(dateStr);
            }
        },
        [availableDates]
    );

    const handleMatchesLeagueChange = useCallback(
        (value: number | string | undefined) => {
            if (value === "all") {
                setSelectedCupLeagueId("all");
                resetDates();
            } else if (typeof value === "number") {
                setSelectedCupLeagueId(value);
                resetDates();
            }
        },
        [resetDates]
    );

    return {
        selectedCupLeagueId,
        availableDateObjects,
        selectedDate,
        selectedDateLabel,
        displayMatches,
        handleMatchesLeagueChange,
        handlePrevDate,
        handleNextDate,
        handleDateSelect,
    };
}

